import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SearchResult, SearchType } from "@akhshy/types";
import { api } from "../../lib/api.ts";

/** Where each result type is edited in the dashboard. */
const ROUTE: Record<SearchType, string> = {
  vault: "/vault",
  prompt: "/prompts",
  ritual: "/rituals",
};

const GROUP_LABEL: Record<SearchType, string> = {
  vault: "Vault",
  prompt: "Prompts",
  ritual: "Rituals",
};

const GROUP_ORDER: SearchType[] = ["vault", "prompt", "ritual"];

function resultMeta(r: SearchResult): string {
  switch (r.type) {
    case "vault":
      return r.kind;
    case "prompt":
      return r.useCase;
    case "ritual":
      return `week ${r.week}`;
  }
}

/**
 * Global ⌘K / `/` command palette for the dashboard. Searches published vault,
 * prompt and ritual content and jumps to the matching editor section.
 */
export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const k = e.key.toLowerCase();
      const meta = e.metaKey || e.ctrlKey;
      if (meta && k === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (e.key === "/" && !open) {
        const el = document.activeElement;
        const typing =
          el instanceof HTMLInputElement ||
          el instanceof HTMLTextAreaElement ||
          (el instanceof HTMLElement && el.isContentEditable);
        if (!typing) {
          e.preventDefault();
          setOpen(true);
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
    setQ("");
    setResults([]);
  }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let alive = true;
    const t = setTimeout(() => {
      api
        .search(term, { limit: 20 })
        .then((res) => {
          if (alive) {
            setResults(res.items);
            setActive(0);
          }
        })
        .catch(() => {
          if (alive) setResults([]);
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 180);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q]);

  const { grouped, flat } = useMemo(() => {
    const grouped: { type: SearchType; items: SearchResult[] }[] = [];
    const flat: SearchResult[] = [];
    for (const type of GROUP_ORDER) {
      const items = results.filter((r) => r.type === type);
      if (items.length) {
        grouped.push({ type, items });
        flat.push(...items);
      }
    }
    return { grouped, flat };
  }, [results]);

  function close() {
    setOpen(false);
  }

  function choose(r: SearchResult) {
    close();
    navigate(ROUTE[r.type]);
  }

  function onInputKey(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = flat[active];
      if (r) choose(r);
    }
  }

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-idx="${active}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  if (!open) return null;

  return (
    <div
      className="cmdk-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Search content"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="cmdk-panel">
        <div className="cmdk-input-row">
          <span className="cmdk-search-icon" aria-hidden="true">
            ⌕
          </span>
          <input
            ref={inputRef}
            className="cmdk-input"
            type="text"
            value={q}
            placeholder="Search vault, prompts, rituals…"
            aria-label="Search query"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
          />
          <kbd className="cmdk-esc">esc</kbd>
        </div>

        <div className="cmdk-results" ref={listRef}>
          {loading && flat.length === 0 && (
            <p className="cmdk-hint">searching…</p>
          )}
          {!loading && q.trim() && flat.length === 0 && (
            <p className="cmdk-hint">no published matches for “{q.trim()}”</p>
          )}
          {!q.trim() && (
            <p className="cmdk-hint">
              type to search across vault, prompts and rituals
            </p>
          )}
          {grouped.map((group) => (
            <div key={group.type} className="cmdk-group">
              <div className="cmdk-group-label">{GROUP_LABEL[group.type]}</div>
              {group.items.map((r) => {
                const idx = flat.indexOf(r);
                return (
                  <button
                    key={`${r.type}-${r.id}`}
                    type="button"
                    data-idx={idx}
                    className={`cmdk-item${idx === active ? " is-active" : ""}`}
                    onMouseMove={() => setActive(idx)}
                    onClick={() => choose(r)}
                  >
                    <span className="cmdk-item-title">{r.title}</span>
                    <span className="cmdk-item-meta">{resultMeta(r)}</span>
                    {r.snippet && (
                      <span className="cmdk-item-snippet">{r.snippet}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="cmdk-footer">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
          <span>
            <kbd>⌘</kbd>
            <kbd>K</kbd> toggle
          </span>
        </div>
      </div>
    </div>
  );
}
